import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { setMode } from "@/lib/api";
import {
  ShieldCheck, Camera, Clock, Scale, ArrowRight, Lock, MapPin, FileText,
  PlayCircle, Sparkles, Stethoscope, Plane, BadgeDollarSign, Radar, HeartPulse,
  Gavel, TrendingUp, Wallet, CalendarClock,
} from "lucide-react";
import { EnterpriseCrmSection } from "@/components/EnterpriseCrm";

const FEATURES = [
  { icon: Camera, title: "Capture Evidence Instantly", desc: "Photos, video, and audio auto-sorted into the right folders, timestamped and geotagged." },
  { icon: Clock, title: "Automatic Timeline", desc: "Every photo, treatment, and call lands on a clean chronological record of your case." },
  { icon: Scale, title: "Connect With Attorneys", desc: "Request a referral and share a complete, organized case file with one tap." },
  { icon: Lock, title: "Private & Secure", desc: "Role-based access and secure cloud storage keep your case file protected." },
];

const STEPS = [
  { n: "01", t: "Stay safe & document", d: "Check safety, call 911, and capture your GPS location automatically." },
  { n: "02", t: "Collect the facts", d: "Driver, witness, police, medical, and insurance details in a guided wizard." },
  { n: "03", t: "Build your case", d: "Upload evidence and get an organized file ready to share with an attorney." },
];

const ATTORNEY_POINTS = [
  { icon: Sparkles, t: "Verified, scored cases", d: "Every marketplace case carries a Crash Ready Trust Score backed by AI-verified documents and evidence." },
  { icon: HeartPulse, t: "RN-vetted Gold cases", d: "Licensed triage nurses review injuries before you claim — know the medical picture up front." },
  { icon: Gavel, t: "Firm command center", d: "Retainers, e-sign, demand builder, evidence intelligence and secure client messaging in one portal." },
  { icon: TrendingUp, t: "Predictable pipeline", d: "Membership plans with transparent claim fees. Claim only the cases that fit your practice." },
];

const PILOT_POINTS = [
  { icon: Radar, t: "Dispatches near you", d: "Accident-scene deployment offers routed to FAA-certified pilots by location and readiness." },
  { icon: BadgeDollarSign, t: "Paid per deployment", d: "Transparent fees per completed mission with a weekly payout ledger." },
  { icon: Plane, t: "Built for the field", d: "PWA pilot HUD with wearables companion, weather and geofencing checks on scene." },
];

const NURSE_POINTS = [
  { icon: Stethoscope, t: "Remote triage intakes", d: "Guided intake workflows with e-sign consent — first-accept locking means the case is yours." },
  { icon: Wallet, t: "Weekly payouts", d: "Per-intake compensation with a transparent earnings ledger and yearly 1099 statements." },
  { icon: CalendarClock, t: "Work on your schedule", d: "Accept intakes when you're available. Every call and note is tracked for your records." },
];

const TELEHEALTH_POINTS = [
  { icon: HeartPulse, t: "Attorney-referred patients", d: "Receive eligibility-screened accident patients from law firms — scheduling, consents and charting handled in one portal." },
  { icon: FileText, t: "Charts that move cases", d: "Upload visit charts once; attorneys acknowledge receipt and AI builds the medical chronology automatically." },
  { icon: CalendarClock, t: "Your practice, your rules", d: "Set states, specialties, availability and visit modes. Credential wallet keeps your licenses verified and current." },
];

const RoleSection = ({ id, eyebrow, title, desc, points, cta, ctaTo, dark, testid }) => (
  <section id={id} className={dark ? "bg-navy text-white" : "bg-white"} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <p className={`text-xs font-semibold uppercase tracking-widest ${dark ? "text-cyan-300" : "text-cobalt"}`}>{eyebrow}</p>
      <div className="mt-2 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <h2 className={`font-heading text-2xl md:text-3xl font-bold tracking-tight max-w-2xl ${dark ? "text-white" : "text-navy"}`}>{title}</h2>
        <Link to={ctaTo} data-testid={`${testid}-cta`}
          className={`inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full transition-all active:scale-95 shrink-0 ${dark ? "bg-white text-navy hover:bg-slate-100" : "bg-navy text-white hover:opacity-90"}`}>
          {cta} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <p className={`mt-3 max-w-2xl text-sm ${dark ? "text-slate-300" : "text-slate-600"}`}>{desc}</p>
      <div className={`mt-10 grid sm:grid-cols-2 ${points.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-6`}>
        {points.map(({ icon: Icon, t, d }) => (
          <div key={t} className={`rounded-3xl p-6 transition-all hover:-translate-y-0.5 ${dark ? "bg-white/5 border border-white/10" : "bg-slate-50 border border-slate-100"}`}>
            <div className={`h-11 w-11 rounded-2xl flex items-center justify-center mb-4 ${dark ? "bg-cyan-400/15" : "bg-cobalt-light"}`}>
              <Icon className={`w-5 h-5 ${dark ? "text-cyan-300" : "text-cobalt"}`} />
            </div>
            <h3 className={`font-heading font-semibold ${dark ? "text-white" : "text-navy"}`}>{t}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-slate-300" : "text-slate-600"}`}>{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Landing() {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);

  useEffect(() => {
    api.get("/demo/scenarios").then(({ data }) => setScenarios(data.scenarios)).catch(() => {});
  }, []);

  const openDemo = () => {
    sessionStorage.setItem("cr_demo_source", "landing");
    setMode("DEMO");
    navigate("/demo");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-navy flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight text-navy">Crash Ready</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={openDemo} data-testid="nav-demo" className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 hover:text-navy">
              <PlayCircle className="w-4 h-4 text-cobalt" /> Try the demo
            </button>
            <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-navy">Log in</Link>
            <Link to="/register" data-testid="nav-signup" className="px-5 py-2.5 text-sm font-medium text-white bg-cobalt hover:bg-cobalt-hover rounded-full transition-all shadow-md hover:shadow-lg">Get started</Link>
          </div>
        </div>
      </header>

      {/* Hero — clients first */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="cr-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cobalt-light text-cobalt text-xs font-semibold uppercase tracking-widest mb-6">
              <MapPin className="w-3.5 h-3.5" /> Built for the moments that matter
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-navy leading-[1.05]">
              The first 30 minutes after a crash decide everything.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Crash Ready guides you step-by-step right at the scene — documenting evidence,
              organizing your information, and connecting you with qualified attorneys.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="hero-get-started" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-medium text-white bg-cobalt hover:bg-cobalt-hover rounded-full transition-all shadow-md hover:shadow-lg active:scale-95">
                Start a report <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={openDemo} data-testid="hero-try-demo" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-base font-medium text-navy bg-white border border-slate-200 hover:border-cobalt hover:text-cobalt rounded-full transition-all active:scale-95">
                <PlayCircle className="w-5 h-5" /> Try the demo — no signup
              </button>
            </div>
            <p className="mt-5 text-xs text-slate-400 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Crash Ready provides documentation tools, not legal advice.</p>
          </div>

          <div className="relative cr-fade-up" style={{ animationDelay: "0.1s" }}>
            <div className="rounded-3xl overflow-hidden shadow-[0_20px_60px_rgb(10,25,47,0.15)] border border-slate-100">
              <img src="https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Driver" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 hidden sm:flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-green-50 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-green-600" /></div>
              <div>
                <div className="text-sm font-semibold text-navy">Evidence secured</div>
                <div className="text-xs text-slate-500">Timestamped & geotagged</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client demo section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="landing-demo-section">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-widest">
            <PlayCircle className="w-3.5 h-3.5" /> Interactive demo
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold tracking-tight text-navy">See exactly what happens after a crash — before you ever need it</h2>
          <p className="mt-3 text-slate-600">Walk through a real accident scenario with sample data. No account, no real report, and nobody is contacted — just you exploring how Crash Ready protects your case.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {scenarios.map((sc) => (
            <button key={sc.id} onClick={openDemo} data-testid={`landing-demo-${sc.id}`}
              className="group text-left bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-xl transition-all">
              <div className="relative">
                <img src={sc.photo} alt={sc.label} className="h-40 w-full object-cover" />
                <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-navy px-2 py-1 rounded-full">Sample Demo Data</span>
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold text-navy">{sc.label}</h3>
                <p className="text-sm text-slate-500 mt-1">{sc.tagline}</p>
                <div className="mt-3 text-xs font-semibold text-cobalt flex items-center gap-1">Explore this scenario <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" /></div>
              </div>
            </button>
          ))}
        </div>
        <div className="mt-8 text-center">
          <button onClick={openDemo} data-testid="landing-demo-cta"
            className="inline-flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-navy hover:opacity-90 rounded-full transition-all active:scale-95">
            <PlayCircle className="w-5 h-5" /> Launch the demo
          </button>
          <p className="mt-3 text-xs text-slate-400">No professionals are contacted and nothing is saved in Demo Mode.</p>
        </div>
      </section>

      {/* Client features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-navy text-center">Everything you need, organized for you</h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-2xl bg-cobalt-light flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-cobalt" /></div>
              <h3 className="font-heading font-semibold text-navy">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Client steps */}
      <section className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-center">Three steps to a protected case</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n}>
                <div className="font-heading text-5xl font-extrabold text-cobalt/80">{s.n}</div>
                <h3 className="mt-4 font-heading font-semibold text-xl">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 text-center">
            <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 text-base font-medium text-navy bg-white hover:bg-slate-100 rounded-full transition-all active:scale-95">
              Create your free account <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Attorneys */}
      <RoleSection
        id="attorneys" testid="landing-attorneys"
        eyebrow="For Attorneys"
        title="Signed-quality cases, verified before you ever claim them."
        desc="Stop chasing unqualified leads. Crash Ready delivers documented, AI-verified, RN-triaged accident cases with a Trust Score — plus a full firm toolkit to run them."
        points={ATTORNEY_POINTS}
        cta="Join as an attorney" ctaTo="/register?role=attorney"
      />

      {/* Enterprise CRM compatibility */}
      <section id="enterprise-crm" className="bg-slate-50 border-y border-slate-100" data-testid="landing-enterprise-crm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <EnterpriseCrmSection />
        </div>
      </section>

      {/* Drone Pilots */}
      <RoleSection
        id="pilots" testid="landing-pilots" dark
        eyebrow="For Drone Pilots"
        title="Turn your FAA certification into paid accident-scene missions."
        desc="Join the Crash Ready Response Network and receive deployment offers for scene documentation flights in your area."
        points={PILOT_POINTS}
        cta="Apply as a pilot" ctaTo="/register?role=pilot"
      />

      {/* Registered Nurses */}
      <RoleSection
        id="nurses" testid="landing-nurses"
        eyebrow="For Registered Nurses"
        title="Put your clinical judgment to work — remote triage that changes case outcomes."
        desc="The RN Vetting Network pays licensed nurses to triage accident victims remotely, guide them to the right care, and elevate cases to Gold-badge status."
        points={NURSE_POINTS}
        cta="Apply as an RN" ctaTo="/register?role=nurse"
      />

      {/* Telehealth Providers */}
      <RoleSection
        id="telehealth" testid="landing-telehealth" dark
        eyebrow="For Telehealth Providers"
        title="Grow your practice with attorney-referred accident patients."
        desc="Join the Crash Ready Telehealth Network. Law firms refer documented accident victims directly to your practice — with scheduling, consents, charting and AI medical intelligence built in."
        points={TELEHEALTH_POINTS}
        cta="Apply as a telehealth provider" ctaTo="/register?role=telehealth"
      />

      <footer className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-center text-sm text-slate-400">
        © {new Date().getFullYear()} Crash Ready. Documentation tools only — not legal advice.
      </footer>
    </div>
  );
}
